import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useNavigate, useParams } from 'react-router-dom'
import { ReceptorsAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { FormBody } from './AddWorkflowEvidenceForm.styles'

export type Option = {
  name: string
  pid: string
  pname: string
  tid: string
  tname: string
  tpid: string
}

export type WorkflowFormState = {
  project: string
  taskType: string
  taskName: string
  title: string
  description: string
  siteUrl: string
  label: string
}

export type AddWorkflowEvidenceFormProps = {
  modalId: ModalFormId
  isShown: boolean
  onSubmitFunc: (data: WorkflowFormState) => Promise<void> | void
  configs?: Option[]
  siteUrl: string
  label: string
  controlId: string
  description: string
  activeReceptor?: ReceptorRecord
  name?: string
}

export const AddWorkflowEvidenceForm: React.FC<
  AddWorkflowEvidenceFormProps
> = ({
  modalId,
  isShown,
  onSubmitFunc,
  configs = [],
  siteUrl = '',
  label = '',
  controlId = '',
  description = '',
  activeReceptor,
  name = '',
}) => {
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const receptorUrl = useMemo(() => {
    if (!activeReceptor || !pageContext) return ''
    return `/${pageContext}/${
      ReceptorsAbsoluteRoutes.SHOW
    }/${activeReceptor.getId()}`
  }, [activeReceptor, pageContext])
  const makeInitial = useCallback(
    (
      siteUrl?: string,
      label?: string,
      controlId?: string,
    ): WorkflowFormState => ({
      project: '',
      taskType: '',
      taskName: '',
      title: `[${controlId}] Request for evidence`,
      description: description || '',
      siteUrl: siteUrl || '',
      label: label || '',
    }),
    [description],
  )

  const [state, setState] = useState<WorkflowFormState>(() =>
    makeInitial(siteUrl, label, controlId),
  )

  const selectedIndex = useMemo(
    () =>
      configs.findIndex(
        (c) => c.pid === state.project && c.tid === state.taskType,
      ),
    [configs, state.project, state.taskType],
  )

  // Reset when modal closes; when modal is open, keep title/description/siteUrl/label in sync
  // so we pick up resolved control id once useControlModel returns (instead of staying on OID).
  useEffect(() => {
    const next = makeInitial(siteUrl, label, controlId)
    if (!isShown) {
      setState(next)
    } else {
      setState((prev) => ({
        ...prev,
        title: next.title,
        description: next.description,
        siteUrl: next.siteUrl,
        label: next.label,
      }))
    }
  }, [isShown, siteUrl, label, controlId, description, makeInitial])

  // Initialize default from first config if none selected yet
  useEffect(() => {
    if (configs.length > 0 && (!state.project || !state.taskType)) {
      setState((s) => ({
        ...s,
        project: s.project || configs[0].pid,
        taskType: s.taskType || configs[0].tid,
        taskName: s.taskName || configs[0].tname,
      }))
    }
  }, [configs, state.project, state.taskType])

  useEffect(() => {
    if (selectedIndex < 0) {
      setState((s) => ({
        ...s,
        project: '',
        taskType: '',
        taskName: '',
      }))
    }
  }, [selectedIndex])

  const configItems: SelectItem[] = useMemo(
    () =>
      configs.map((c, i) => ({
        name: `${c.name} (${c.pname} - ${c.tname})`,
        value: i,
      })),
    [configs],
  )

  const configDefaultValues: SelectItem[] = useMemo(() => {
    if (selectedIndex < 0 || !configs[selectedIndex]) return []
    const c = configs[selectedIndex]
    return [
      { name: `${c.name} (${c.pname} - ${c.tname})`, value: selectedIndex },
    ]
  }, [configs, selectedIndex])

  const handleConfigSelect = useCallback(
    (selected: SelectItem[]) => {
      const item = selected[0]
      if (item == null) return
      const idx = Number(item.value)
      const sel = configs[idx]
      if (!sel) return
      setState((s) => ({
        ...s,
        project: sel.pid,
        taskType: sel.tid,
        taskName: sel.tname,
      }))
    },
    [configs],
  )

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!state.project || !state.title?.trim()) return
    await onSubmitFunc(state)
  }

  return (
    <FormBody id={modalId} onSubmit={onSubmit}>
      {configs.length > 0 ? (
        <SelectDropdown
          label="Configuration"
          items={configItems}
          defaultValues={configDefaultValues}
          onSelectCb={handleConfigSelect}
          placeholder="Select configuration"
          hideError
        />
      ) : (
        <Message
          messageType={MessageType.ERROR}
          messageText={`No ${name} configurations found`}
          ctaButton={
            receptorUrl
              ? {
                  text: 'Configure',
                  onClick: (
                    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                  ) => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(receptorUrl)
                  },
                }
              : undefined
          }
          fullWidth
        />
      )}
    </FormBody>
  )
}
